import { getExtension, resolveId } from "@medplum/core";
import { QuestionnaireResponse, QuestionnaireResponseItem } from "@medplum/fhirtypes";

import {
  ClosedOutPregnancyDatingSummary,
  ClosedOutQuestionAnswer,
  ClosedOutSectionData,
} from "@/clients/types/closed-out-pregnancy-dating";
import { Extensions } from "@/fhir/enums/extensions";
import { usDateFormat } from "@/utils/datetime";

const EDD_DATING_LINK_IDS = new Set([
  "lmpDate",
  "lmpEddDate",
  "customEddDate",
  "ultrasoundEddDate",
  "ultrasoundDate",
]);

/**
 * Returns `yyyy-MM-dd` from a FHIR date or dateTime string.
 *
 * @param value - ISO date or dateTime
 * @returns Date-only string, or `undefined` when empty
 */
export function toDateOnly(value: string | undefined): string | undefined {
  if (!value) return undefined;
  return value.slice(0, 10);
}

/**
 * True when questionnaire items already include answered EDD dating fields.
 *
 * @param items - Top-level QuestionnaireResponse items
 */
export function questionnaireItemsHaveEddDating(
  items: QuestionnaireResponseItem[] | undefined,
): boolean {
  const eddGroup = items?.find((item) => item.linkId === "eddGroup");
  if (!eddGroup?.item?.length) return false;

  return eddGroup.item.some(
    (item) => EDD_DATING_LINK_IDS.has(item.linkId) && (item.answer?.length ?? 0) > 0,
  );
}

/**
 * Builds an `eddGroup` QuestionnaireResponse item from pregnancy dating Observations.
 *
 * @param summary - LMP / EDD ISO values from the pregnancy record
 * @returns `eddGroup` item, or `undefined` when no dating values exist
 */
export function buildEddGroupItemFromDatingSummary(
  summary?: ClosedOutPregnancyDatingSummary,
): QuestionnaireResponseItem | undefined {
  const nestedItems: QuestionnaireResponseItem[] = [];
  const lmpDate = toDateOnly(summary?.lastMenstrualPeriod);
  const eddDate = toDateOnly(summary?.edd);

  if (lmpDate) {
    nestedItems.push({
      linkId: "lmpDate",
      text: "LMP",
      answer: [{ valueDate: lmpDate }],
    });
  }

  if (eddDate) {
    nestedItems.push({
      linkId: "lmpEddDate",
      text: "EDD",
      answer: [{ valueDate: eddDate }],
    });
  }

  if (nestedItems.length === 0) return undefined;

  return {
    linkId: "eddGroup",
    text: "EDD Info",
    item: nestedItems,
  };
}

/**
 * Maps pregnancy dating values to Client History section rows (same shape as QR parsing).
 *
 * @param summary - LMP / EDD ISO values from the pregnancy record
 * @returns EDD Info section, or empty array when nothing to show
 */
export function buildEddSectionsFromDatingSummary(
  summary?: ClosedOutPregnancyDatingSummary,
): ClosedOutSectionData[] {
  const questions: ClosedOutQuestionAnswer[] = [];
  const lmpDate = toDateOnly(summary?.lastMenstrualPeriod);
  const eddDate = toDateOnly(summary?.edd);

  if (lmpDate) {
    questions.push({
      linkId: "lmpDate",
      question: "LMP",
      answer: usDateFormat(lmpDate),
    });
  }

  if (eddDate) {
    questions.push({
      linkId: "lmpEddDate",
      question: "EDD",
      answer: usDateFormat(eddDate),
    });
  }

  if (questions.length === 0) return [];

  return [
    {
      linkId: "eddGroup",
      title: "EDD Info",
      questions,
    },
  ];
}

/**
 * Merges observation-derived `eddGroup` into closeout items when dating is missing.
 *
 * @param items - Existing closeout items (intake currentPregnancy + EDD QR)
 * @param eddGroupFromObservations - Optional eddGroup built from Observations
 */
export function mergeCloseoutItemsWithObservationEdd(
  items: QuestionnaireResponseItem[],
  eddGroupFromObservations: QuestionnaireResponseItem | undefined,
): QuestionnaireResponseItem[] {
  if (!eddGroupFromObservations || questionnaireItemsHaveEddDating(items)) {
    return items;
  }

  const withoutEmptyEddGroup = items.filter((item) => item.linkId !== "eddGroup");
  return [...withoutEmptyEddGroup, eddGroupFromObservations];
}

/**
 * Reads the pregnancy `List` id stored on a pregnancy-closeout QuestionnaireResponse.
 *
 * @param questionnaireResponse - Closeout QR
 * @returns List id, or `undefined` when missing/malformed
 */
export function getPregnancyRecordListIdFromCloseout(
  questionnaireResponse: QuestionnaireResponse,
): string | undefined {
  const extension = getExtension(
    questionnaireResponse,
    Extensions.PregnancyCloseoutPregnancyRecord,
  );
  return resolveId(extension?.valueReference);
}
